"use client";

import { Maximize2Icon, Minimize2Icon, XIcon } from "lucide-react";
import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import type { BrowserViewportResizeDirection } from "~/browser/browserViewportLayout";
import { Button } from "~/components/ui/button";
import { Tooltip, TooltipPopup, TooltipTrigger } from "~/components/ui/tooltip";
import { useFloatingFigureStore } from "~/floatingFigureStore";
import { cn } from "~/lib/utils";

import ChatMarkdown from "../ChatMarkdown";
import {
  measureMiniPlayerLayout,
  MINI_PLAYER_RESIZE_HANDLES,
  sameMiniPlayerLayout,
  type MiniPlayerLayout,
} from "../preview/miniPlayerChrome";
import {
  clampPreviewMiniPlayerPosition,
  NO_PREVIEW_MINI_PLAYER_OBSTACLES,
  type PreviewMiniPlayerFrame,
  resizePreviewMiniPlayer,
  resolvePreviewMiniPlayerFrame,
} from "../preview/previewMiniPlayerLayout";
import { FigureHostContext } from "./figureHost";
import { WorkspaceHtmlPage } from "./WorkspaceHtmlPage";
import { floatingFigureFrameSource, resolveMaximizedFigureFrame } from "./floatingFigureLayout";

interface PointerGesture {
  readonly pointerId: number;
  readonly target: HTMLElement;
  readonly pointerX: number;
  readonly pointerY: number;
  readonly frame: PreviewMiniPlayerFrame;
  readonly direction: BrowserViewportResizeDirection | null;
  readonly originalWidth: number | null;
  readonly originalPosition: { readonly x: number; readonly y: number } | null;
}

/**
 * A chat figure floated over the chat column: drag it by its title bar,
 * resize it from any edge (it keeps the figure's shape), park it in the
 * margins beside the composer, or maximise it over the column. It uses the
 * browser mini player's layout rules, so both windows behave alike.
 */
export function FloatingFigureWindow({
  composerOverlayElement,
}: {
  readonly composerOverlayElement: HTMLElement | null;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const gestureRef = useRef<PointerGesture | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);
  const [layout, setLayout] = useState<MiniPlayerLayout | null>(null);
  const figure = useFloatingFigureStore((state) => state.figure);
  const width = useFloatingFigureStore((state) => state.width);
  const position = useFloatingFigureStore((state) => state.position);
  const maximized = useFloatingFigureStore((state) => state.maximized);
  const container = layout?.container ?? null;
  const obstacles = layout?.obstacles ?? NO_PREVIEW_MINI_PLAYER_OBSTACLES;
  const source = figure ? floatingFigureFrameSource(figure.source) : null;
  const frame =
    container && source
      ? maximized
        ? resolveMaximizedFigureFrame(container, obstacles)
        : resolvePreviewMiniPlayerFrame({ width, position, source, container, obstacles })
      : null;

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const measure = () => {
      const next = measureMiniPlayerLayout(element, composerOverlayElement);
      setLayout((current) => (current && sameMiniPlayerLayout(current, next) ? current : next));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    if (composerOverlayElement) observer.observe(composerOverlayElement);
    return () => observer.disconnect();
  }, [composerOverlayElement]);

  // A figure floats from its toolbar button, so the reader is not typing:
  // take focus into the window so Escape closes it straight away.
  const figureId = figure?.id ?? null;
  const framed = frame !== null;
  useEffect(() => {
    if (figureId !== null && framed) sectionRef.current?.focus({ preventScroll: true });
  }, [figureId, framed]);

  const cancelGesture = useCallback(() => {
    const gesture = gestureRef.current;
    if (!gesture) return;
    gestureRef.current = null;
    const store = useFloatingFigureStore.getState();
    store.resize(gesture.originalWidth);
    store.move(gesture.originalPosition);
    if (gesture.target.hasPointerCapture(gesture.pointerId)) {
      gesture.target.releasePointerCapture(gesture.pointerId);
    }
  }, []);

  useLayoutEffect(() => {
    window.addEventListener("blur", cancelGesture);
    return () => {
      window.removeEventListener("blur", cancelGesture);
      cancelGesture();
    };
  }, [cancelGesture]);

  const beginGesture = (
    event: ReactPointerEvent<HTMLElement>,
    direction: BrowserViewportResizeDirection | null,
  ) => {
    if (event.button !== 0 || !frame || maximized) return;
    gestureRef.current = {
      pointerId: event.pointerId,
      target: event.currentTarget,
      pointerX: event.clientX,
      pointerY: event.clientY,
      frame,
      direction,
      originalWidth: width,
      originalPosition: position,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
    event.stopPropagation();
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId || !container || !source) return;
    const delta = { x: event.clientX - gesture.pointerX, y: event.clientY - gesture.pointerY };
    const store = useFloatingFigureStore.getState();
    if (gesture.direction === null) {
      store.resize(gesture.frame.width);
      store.move(
        clampPreviewMiniPlayerPosition(
          { x: gesture.frame.x + delta.x, y: gesture.frame.y + delta.y },
          container,
          gesture.frame,
          obstacles,
        ),
      );
      return;
    }
    const next = resizePreviewMiniPlayer({
      start: gesture.frame,
      direction: gesture.direction,
      delta,
      source,
      container,
      obstacles,
    });
    store.resize(next.width);
    store.move({ x: next.x, y: next.y });
  };

  const endGesture = (event: ReactPointerEvent<HTMLElement>) => {
    if (gestureRef.current?.pointerId !== event.pointerId) return;
    if (event.type !== "pointerup") {
      cancelGesture();
      return;
    }
    handlePointerMove(event);
    gestureRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const close = () => useFloatingFigureStore.getState().close();
  const toggleMaximized = () => useFloatingFigureStore.getState().setMaximized(!maximized);
  const gestureHandlers = {
    onPointerMove: handlePointerMove,
    onPointerUp: endGesture,
    onPointerCancel: endGesture,
    onLostPointerCapture: endGesture,
  };

  return (
    <div ref={containerRef} className="pointer-events-none absolute inset-0 z-30">
      {figure && frame ? (
        <section
          key={figure.id}
          ref={sectionRef}
          tabIndex={-1}
          aria-label={figure.title ? `Floating figure: ${figure.title}` : "Floating figure"}
          data-floating-figure={figure.id}
          className="floating-figure pointer-events-auto absolute flex flex-col overflow-visible rounded-xl outline-none bg-popover text-popover-foreground shadow-2xl/35 ring-1 ring-border/80"
          style={{ left: frame.x, top: frame.y, width: frame.width, height: frame.height }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.stopPropagation();
              close();
            }
          }}
        >
          <header
            className={cn(
              "flex h-8 shrink-0 select-none items-center gap-1 border-b border-border/60 ps-3 pe-1 touch-none",
              maximized ? "cursor-default" : "cursor-grab active:cursor-grabbing",
            )}
            data-floating-figure-drag="true"
            onPointerDown={(event) => {
              // The title bar is not focusable; grabbing it focuses the
              // window so Escape still reaches it.
              sectionRef.current?.focus({ preventScroll: true });
              beginGesture(event, null);
            }}
            onDoubleClick={toggleMaximized}
            {...gestureHandlers}
          >
            <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground/80">
              {figure.title ??
                (figure.kind === "page"
                  ? "Page"
                  : figure.language === "chart"
                    ? "Chart"
                    : "Diagram")}
            </span>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={maximized ? "Restore floating figure" : "Maximize floating figure"}
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={toggleMaximized}
                  />
                }
              >
                {maximized ? <Minimize2Icon /> : <Maximize2Icon />}
              </TooltipTrigger>
              <TooltipPopup side="top">{maximized ? "Restore" : "Maximize"}</TooltipPopup>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Close floating figure"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={close}
                  />
                }
              >
                <XIcon />
              </TooltipTrigger>
              <TooltipPopup side="top">Close</TooltipPopup>
            </Tooltip>
          </header>
          <div className="floating-figure-body min-h-0 flex-1 overflow-hidden px-3 pb-2 pt-1">
            <FigureHostContext value="floating">
              {figure.kind === "page" ? (
                <WorkspaceHtmlPage
                  threadRef={figure.threadRef}
                  path={figure.path}
                  panelPath={null}
                  alt={figure.title ?? undefined}
                  isStreaming={false}
                />
              ) : (
                <ChatMarkdown
                  cwd={undefined}
                  text={`\`\`\`${figure.language}\n${figure.code}\n\`\`\``}
                />
              )}
            </FigureHostContext>
          </div>
          {maximized
            ? null
            : MINI_PLAYER_RESIZE_HANDLES.map(({ direction, className }) => (
                <div
                  key={direction}
                  role="presentation"
                  data-floating-figure-resize={direction}
                  className={cn("absolute z-[31] touch-none", className)}
                  onPointerDown={(event) => beginGesture(event, direction)}
                  {...gestureHandlers}
                />
              ))}
        </section>
      ) : null}
    </div>
  );
}
